import { CommonModule } from '@angular/common';
import {
  ChangeDetectionStrategy,
  Component,
  EventEmitter,
  Input,
  OnChanges,
  Output,
  SimpleChanges,
} from '@angular/core';
import { FormsModule } from '@angular/forms';
import { MatDividerModule } from '@angular/material/divider';
import { MatLegacyMenuModule } from '@angular/material/legacy-menu';
import { DateTime } from '@mode-switch/express';
import {
  EncDiscreteBooleanFilterField,
  EncDiscreteFilterField,
  EncFilterField,
  EncTopByFieldCondition,
  Encoding,
  FilterType,
  SelectionMode,
} from '@mode-vegas/vegas';
import { IconButtonComponent, IconComponent } from '@mode/capra';
import { FeatureFlag, FeatureFlagsFacade, FlamingoTypes } from '@mode/shared/contract-common';
import { deepCopy } from '@mode/shared/util-js';
import { CompiledField, FormulaOperations } from '@mode/shared/util-switch';
import { Observable } from 'rxjs';
import { RankPanelComponent } from '../../../rank-panel/rank-panel.component';
import * as util from '../../filter-modal-helpers';
import { FilterStep } from '../../filter-modal.component';
import { FilterValueListComponent } from './filter-value-list/filter-value-list.component';

type DiscreteTypedFilterField = EncDiscreteFilterField | EncDiscreteBooleanFilterField;
function isDiscreteTypedFilter(filter: EncFilterField): filter is DiscreteTypedFilterField {
  switch (filter.filterType) {
    case FilterType.Discrete:
    case FilterType.DiscreteBoolean: {
      return true;
    }
    default: {
      return false;
    }
  }
}

/**
 * This is a composite component that manages both the value list filter
 * and the topN filters. It maintains a radio button selector that controls
 * which panel is visible. Otherwise, it operates as an adapter between
 * the filter-value-list and rank-panel components and the filter-configuration
 * component.
 */
@Component({
  standalone: true,
  imports: [
    CommonModule,
    FormsModule,
    IconButtonComponent,
    IconComponent,
    MatDividerModule,
    MatLegacyMenuModule,
    RankPanelComponent,
    FilterValueListComponent,
  ],
  selector: 'mode-filter-values',
  templateUrl: './filter-values.component.html',
  styleUrls: ['./filter-values.component.scss'],
  changeDetection: ChangeDetectionStrategy.OnPush,
})
export class FilterValuesComponent implements OnChanges {
  @Input() field!: CompiledField;
  @Input() filter!: EncFilterField;
  @Input() view!: FlamingoTypes.ModeView;
  @Input() encoding!: Encoding;
  @Input() step!: FilterStep;
  @Input() queryRunToken!: string;

  @Output() stepChange = new EventEmitter<FilterStep>();
  @Output() filterChange = new EventEmitter<EncFilterField>();
  @Output() isInvalid = new EventEmitter<boolean>();

  readonly FilterStepEnum = FilterStep;

  readonly weekStartOptions = [
    { value: DateTime.WeekStart.Sunday, name: 'Sunday' },
    { value: DateTime.WeekStart.Monday, name: 'Monday' },
    { value: DateTime.WeekStart.Tuesday, name: 'Tuesday' },
    { value: DateTime.WeekStart.Wednesday, name: 'Wednesday' },
    { value: DateTime.WeekStart.Thursday, name: 'Thursday' },
    { value: DateTime.WeekStart.Friday, name: 'Friday' },
    { value: DateTime.WeekStart.Saturday, name: 'Saturday' },
  ];

  readonly yearStartOptions = [
    { value: DateTime.YearStart.January, name: 'January' },
    { value: DateTime.YearStart.February, name: 'February' },
    { value: DateTime.YearStart.March, name: 'March' },
    { value: DateTime.YearStart.April, name: 'April' },
    { value: DateTime.YearStart.May, name: 'May' },
    { value: DateTime.YearStart.June, name: 'June' },
    { value: DateTime.YearStart.July, name: 'July' },
    { value: DateTime.YearStart.August, name: 'August' },
    { value: DateTime.YearStart.September, name: 'September' },
    { value: DateTime.YearStart.October, name: 'October' },
    { value: DateTime.YearStart.November, name: 'November' },
    { value: DateTime.YearStart.December, name: 'December' },
  ];

  showFilterTypeInput = false;
  currentTopNCondition: EncTopByFieldCondition | undefined;

  showRelevantValues = false;
  excludeValues = false;

  weekStart: DateTime.WeekStart | undefined;
  yearStart: DateTime.YearStart | undefined;
  defaultWeekStart: DateTime.WeekStart | undefined;
  defaultYearStart: DateTime.YearStart | undefined;

  public isCustomCalenderSettingsEnabled$: Observable<boolean>;

  constructor(private featureFlagsFacade: FeatureFlagsFacade) {
    this.isCustomCalenderSettingsEnabled$ = this.featureFlagsFacade.asObservable(FeatureFlag.CustomCalendar);
  }

  ngOnChanges(changes: SimpleChanges): void {
    if (changes.field !== undefined) {
      this.showFilterTypeInput = util.supportsTopNFilter(this.field);

      this.weekStart = this.field.weekStart ?? this.field.defaultWeekStart;
      this.yearStart = this.field.yearStart ?? this.field.defaultYearStart;
      this.defaultYearStart = this.field.defaultYearStart;
      this.defaultWeekStart = this.field.defaultWeekStart;
    }

    if (changes.filter !== undefined) {
      if (this.filter?.filterType === FilterType.TopByField) {
        // Sequence was added after the initial TopN release. As a result, some
        // filters may not have a value set. Seed with the default if necessary.
        this.currentTopNCondition = deepCopy(this.filter.cond);
        this.currentTopNCondition.sequence ??= EncTopByFieldCondition.Sequence.FirstIsolate;
      }

      this.showRelevantValues = false;
      this.excludeValues = false;
      if (isDiscreteTypedFilter(this.filter)) {
        this.showRelevantValues = this.filter.showRelevantValuesOnly ?? false;
        this.excludeValues =
          this.filter.selection.mode === SelectionMode.EXCLUDE ||
          this.filter.selection.mode === SelectionMode.EXCLUDE_ALL;
      }
    }
  }

  /**
   * The filter value list emits the entire filter so
   * we just need to pass it though.
   */
  multiValueFilterChanged(filter: EncFilterField) {
    this.filterChange.emit(filter);
  }

  /**
   * Currently the TopN filter behaves differently in that it
   * only works on the filter condition and not the entire
   * filter. We can insert the new condition into the existing
   * filter and emit that.
   * In the future when the old filter panel is retired, we can
   * rework the rank-panel to behave consistently.
   */
  topNFilterConditionChanged(cond: EncTopByFieldCondition) {
    const filter = {
      ...this.filter,
      filterType: FilterType.TopByField,
      cond,
    };
    this.filterChange.emit(filter as EncFilterField);
  }

  invalidationChanged(isInvalid: boolean) {
    this.isInvalid.emit(isInvalid);
  }

  onStepChange() {
    // Reset state
    this.isInvalid.emit(false);
    this.stepChange.emit(this.step);
  }

  toggleExclude() {
    if (!isDiscreteTypedFilter(this.filter)) {
      return;
    }

    const mode = {
      [SelectionMode.ALL]: SelectionMode.EXCLUDE_ALL,
      [SelectionMode.INCLUDE]: SelectionMode.EXCLUDE,
      [SelectionMode.EXCLUDE]: SelectionMode.INCLUDE,
      [SelectionMode.EXCLUDE_ALL]: SelectionMode.ALL,
    }[this.filter.selection.mode];

    this.filterChange.emit({
      ...this.filter,
      selection: {
        ...this.filter.selection,
        mode,
      },
    });
  }

  toggleShowRelevantValues() {
    if (!isDiscreteTypedFilter(this.filter)) {
      return;
    }

    const showRelevantValuesOnly = !this.showRelevantValues;
    const filter = { ...this.filter, showRelevantValuesOnly };
    this.filterChange.emit(filter);
  }

  setWeekStart(weekStart: DateTime.WeekStart): void {
    this.setWeekOrYearStart(weekStart, undefined);
  }

  setYearStart(yearStart: DateTime.YearStart): void {
    this.setWeekOrYearStart(this.weekStart, yearStart);
  }

  private setWeekOrYearStart(weekStart?: DateTime.WeekStart, yearStart?: DateTime.YearStart): void {
    if (this.field.dateFuncUnit) {
      const dateFunc = this.field.hasDateTrunc ? FormulaOperations.dateTrunc : FormulaOperations.datePart;
      const formula = dateFunc(this.field, this.field.dateFuncUnit, weekStart, yearStart);

      // No change
      if (formula === this.field.formula) {
        return;
      }

      let filter = this.filter;
      if (filter.filterType === FilterType.Discrete) {
        const mode = {
          [SelectionMode.ALL]: SelectionMode.ALL,
          [SelectionMode.INCLUDE]: SelectionMode.ALL,
          [SelectionMode.EXCLUDE]: SelectionMode.EXCLUDE_ALL,
          [SelectionMode.EXCLUDE_ALL]: SelectionMode.EXCLUDE_ALL,
        }[filter.selection.mode];

        filter = {
          ...filter,
          selection: {
            ...filter.selection,
            selected: [],
            mode,
          },
        };
      }

      this.filterChange.emit({
        ...filter,
        formula,
      });
    }
  }

  get showFilterSettings(): boolean {
    return this.step === FilterStep.MultiValues || this.showWeekStartOptions || this.showYearStartOptions;
  }

  get showWeekStartOptions(): boolean {
    return this.field.dateFuncUnit === DateTime.Unit.Week;
  }

  get showYearStartOptions(): boolean {
    switch (this.field.dateFuncUnit) {
      case DateTime.Unit.Year:
      case DateTime.Unit.Quarter: {
        return true;
      }
      default: {
        return false;
      }
    }
  }
}
